import React from 'react'
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import './utils/prefetch'
import './services/manualEnrichmentService'
import { enableIOSScrollFix, ensureTouchTargets } from './utils/accessibility'
import { reportWebVitals } from './utils/webVitals'
import { applyReducedMotionCSS, configureGSAPForReducedMotion } from './utils/reducedMotion'
import { setupAppUpdateRecovery } from './utils/appUpdateRecovery'
import { setupRuntimeDiagnostics } from './utils/runtimeDiagnostics'
import { initSentry } from './utils/sentry'

// Initialise Sentry as early as possible so we capture init-time errors too
initSentry();

// Recover from stale-cache / chunk-load failures after deploys (common on Safari)
setupAppUpdateRecovery();

// Apply iOS accessibility fixes on load (lightweight — needed before paint)
enableIOSScrollFix();

// Apply reduced motion CSS (lightweight, affects rendering)
applyReducedMotionCSS();

// Optimize performance on load
const container = document.getElementById("root")!;
const root = createRoot(container);

// Use concurrent features for better loading
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Defer all non-critical bootstrap work until the browser is idle so it
// doesn't extend the longest main-thread task during page load (Lighthouse
// "Max Potential First Input Delay").
const runWhenIdle = (cb: () => void) => {
  const idle = (window as any).requestIdleCallback;
  if (typeof idle === 'function') {
    idle(cb, { timeout: 2000 });
  } else {
    window.setTimeout(cb, 200);
  }
};

runWhenIdle(() => {
  // Diagnostics, GSAP reduced-motion config, touch-target sweep,
  // web vitals, and SW registration are all post-paint concerns.
  setupRuntimeDiagnostics();
  configureGSAPForReducedMotion();
  // utils/analytics was removed 2026-10-11: it beaconed to /api/analytics/*,
  // which this static host answers with index.html and a 200, so every event
  // was discarded while looking sent (the same fault webVitals had). Real
  // usage data goes through services/analyticsService (Supabase RPCs).
  ensureTouchTargets();
  reportWebVitals();

  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration failed silently
    });
  }
});
